import React, {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";

import { DefinedEntity } from "@formant/core/model/DefinedEntity";
import { IView } from "@formant/core/model/IView";
import { Uuid } from "@formant/core/model/Uuid";

import { ConnectedTabItem } from "./F3.ConnectedTabItem";
import {
    Overflow,
    TabsContainer,
    TabsContainerScroll,
} from "./F3.HomeTabs.styles";

interface ITabListProps {
    deviceId: string | undefined;
    filterActive: boolean;
    isSaving: boolean;
    isReorderable: boolean;
    value?: Uuid;
    views: DefinedEntity<IView>[];
    dragViewId?: string | number;
    onChangeViewId?: (id: Uuid) => void;
    onToggleFilter?: () => void;
    onRetrieveOnDemandData?: () => void;
    setIsOverflown: Dispatch<SetStateAction<boolean>>;
    onMoveToStart: (id: Uuid) => Promise<void>;
    onMoveToEnd: (id: Uuid) => Promise<void>;
    onMoveLeft: (id: Uuid) => Promise<void>;
    onMoveRight: (id: Uuid) => Promise<void>;
}

export function TabList(props: ITabListProps) {
    const {
        deviceId,
        filterActive,
        isSaving,
        isReorderable,
        value,
        views,
        dragViewId,
        onChangeViewId,
        onToggleFilter,
        setIsOverflown,
        onMoveToStart,
        onMoveToEnd,
        onMoveLeft,
        onMoveRight,
    } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [tabSizes, setTabSizes] = useState<Record<string, number>>({});
    const [pinnedSide, setPinnedSide] = useState<"left" | "right" | null>(null);
    const [pointerNone, setPointerNone] = useState(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Virtualizer setup
    const rowVirtualizer = useVirtualizer({
        count: views.length,
        getScrollElement: () => containerRef.current,
        estimateSize: index => {
            const view = views[index];
            if (view && tabSizes[view.id]) {
                return tabSizes[view.id];
            }
            return 120; // fallback size
        },
        horizontal: true,
        overscan: 3, // Increase overscan to reduce flickering during size measurements
    });

    const virtualItems = rowVirtualizer.getVirtualItems();

    const selectedView = useMemo(() => {
        return views.find(view => view.id === value);
    }, [views, value]);

    const checkSelectedItemVisibility = useCallback(() => {
        if (!selectedView) {
            return;
        }

        const containerRect = containerRef.current?.getBoundingClientRect();
        const scrollLeft = containerRef.current?.scrollLeft || 0;
        const containerWidth = containerRect?.width || 0;

        // Get the position of the selected item using virtual item's start position
        const virtualItem = virtualItems.find(
            item => views[item.index]?.id === selectedView.id,
        );
        if (!virtualItem) {
            let pinnedSideTemp: "left" | "right" | null = null;
            if (virtualItems[0]) {
                const someItemIndex = virtualItems[0].index;
                const selectedItemIndex = views.findIndex(
                    view => view.id === selectedView.id,
                );
                if (selectedItemIndex !== -1) {
                    pinnedSideTemp =
                        selectedItemIndex < someItemIndex ? "left" : "right";
                }
            }
            setPinnedSide(pinnedSideTemp);
            return;
        }

        const itemStart = virtualItem.start;
        const itemSize = virtualItem.size;
        const itemEnd = itemStart + itemSize;

        // Check if item is out of view
        if (itemStart < scrollLeft) {
            // Out of view on the left
            setPinnedSide("left");
        } else if (itemEnd > scrollLeft + containerWidth) {
            // Out of view on the right
            setPinnedSide("right");
        } else {
            // In view
            setPinnedSide(null);
        }
    }, [selectedView, dragViewId, virtualItems]);

    useEffect(() => {
        checkSelectedItemVisibility();
    }, [checkSelectedItemVisibility]);

    useEffect(() => {
        setIsOverflown(views.length > rowVirtualizer.getVirtualItems().length);
    }, [views, rowVirtualizer, setIsOverflown]);

    useLayoutEffect(() => {
        rowVirtualizer?.measure?.();
    }, [tabSizes, rowVirtualizer]);

    const handleWheel = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setPointerNone(true);
        timeoutRef.current = setTimeout(() => {
            setPointerNone(false);
        }, 500);
    };

    return (
        <TabsContainer>
            <TabsContainerScroll
                onWheel={handleWheel}
                onScroll={checkSelectedItemVisibility}
                ref={containerRef}
            >
                <div
                    style={{
                        height: "auto",
                        width: `${rowVirtualizer.getTotalSize()}px`,
                        position: "relative",
                    }}
                >
                    {virtualItems.map((item, index) => (
                        <div
                            key={views[item.index].id}
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                padding: "0 10px",
                                transform: `translateX(${item.start}px)`,
                            }}
                            ref={el => {
                                if (el) {
                                    // Measure each tab's width and store it
                                    const size =
                                        el.getBoundingClientRect().width;
                                    const id = views[item.index].id;
                                    setTabSizes(prev => {
                                        if (prev[id] === size) {
                                            return prev;
                                        }
                                        return { ...prev, [id]: size };
                                    });
                                }
                            }}
                        >
                            <ConnectedTabItem
                                isStart={index === 0}
                                isEnd={index === views.length - 1}
                                deviceId={deviceId}
                                isReorderable={isReorderable}
                                isSaving={isSaving}
                                isSelected={views[item.index].id === value}
                                filtering={filterActive}
                                onToggleFilter={onToggleFilter}
                                onChangeViewId={onChangeViewId}
                                onMoveToStart={onMoveToStart}
                                onMoveToEnd={onMoveToEnd}
                                onMoveLeft={onMoveLeft}
                                onMoveRight={onMoveRight}
                                data-index={item.index}
                                view={views[item.index]}
                            />
                        </div>
                    ))}
                </div>

                {selectedView && pinnedSide && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            ...(pinnedSide === "left"
                                ? { left: 0 }
                                : { right: 0 }),
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                            pointerEvents: pointerNone ? "none" : "auto",
                        }}
                    >
                        <ConnectedTabItem
                            isStart={false}
                            isEnd={false}
                            deviceId={deviceId}
                            isReorderable={isReorderable}
                            isSaving={isSaving}
                            isSelected={true}
                            filtering={filterActive}
                            onToggleFilter={onToggleFilter}
                            onChangeViewId={onChangeViewId}
                            onMoveToStart={onMoveToStart}
                            onMoveToEnd={onMoveToEnd}
                            onMoveLeft={onMoveLeft}
                            onMoveRight={onMoveRight}
                            view={selectedView}
                        />
                    </div>
                )}
            </TabsContainerScroll>

            <Overflow.LEFT $filtering={filterActive} />
            <Overflow.RIGHT $filtering={filterActive} />
        </TabsContainer>
    );
}
